import { Component, OnInit, OnDestroy } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { FaqService } from 'core/services/faq.service';
import { IFaqCategory, IFaqQuestion } from 'shared/types/faq.interface';
import { IGetPaginatedResponse } from 'shared/types/get-paginated-response.interface';
import { IPagination } from 'shared/types/pagination.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ReplaySubject, of } from 'rxjs';
import { finalize, takeUntil, tap, catchError } from 'rxjs/operators';
import { ContactUsModalComponent } from '../../shared/modal/contact-us-modal/contact-us-modal.component';
import { IFilter } from 'shared/types/filter.interface';
import { ActivatedRoute } from '@angular/router';
import { Faq, SearchResult } from 'shared/types/search-result.type';
import { GoBackComponent } from 'shared/components/go-back/go-back.component';
import { SearchComponent } from '../../blocks/header/search/search.component';
import { KorviuLoaderComponent } from 'shared/components/korviu-loader/korviu-loader.component';
import { KorviuNoDataComponent } from 'shared/components/korviu-no-data/korviu-no-data.component';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { FaqAnswerComponent } from './faq-answer/faq-answer.component';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';

interface IFaqQueryParams {
  questionId: number;
  categoryId: number;
}

export enum SupportedLanguages {
  ENGLISH = 'en',
  SPANISH = 'es'
}

@Component({
  selector: 'app-faq',
  templateUrl: './faq.component.html',
  styleUrl: './faq.component.scss',
  standalone: true,
  imports: [
    GoBackComponent,
    SearchComponent,
    KorviuLoaderComponent,
    KorviuNoDataComponent,
    NzCollapseModule,
    TranslateModule,
    FaqAnswerComponent,
    NzPaginationModule,
    KorviuButtonComponent
  ]
})
export class FaqComponent implements OnInit, OnDestroy {
  public categories: IFaqCategory[];
  public faqCategoriesAndQuestionsMapping: Map<number, IFaqQuestion[]>;
  public isLoading: boolean;
  public total: number;
  public pagination: IPagination;

  public get selectedLanguageId(): number {
    return this._translateService.currentLang === SupportedLanguages.SPANISH ? 2 : 1;
  }

  public searchFn = this._faqService.searchInFAQ.bind(this._faqService);

  private readonly _onDestroy$ = new ReplaySubject<void>(1);

  constructor(
    private _modalService: NzModalService,
    private _faqService: FaqService,
    private _translateService: TranslateService,
    private _route: ActivatedRoute
  ) {
    this.categories = [];
    this.faqCategoriesAndQuestionsMapping = new Map<number, IFaqQuestion[]>();
    this.isLoading = false;
    this.pagination = { page: 1, pageSize: 10 };
  }

  public ngOnInit(): void {
    this._route.queryParams.pipe(takeUntil(this._onDestroy$)).subscribe((params: IFaqQueryParams) => {
      const { categoryId, questionId } = params;
      this._loadFaq(+categoryId, +questionId);
    });
    this._setLangListener();
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  public onActiveChange(question: IFaqQuestion, category: IFaqCategory): void {
    // A mechanism where only one child is open
    /*for (const [categoryId, questions] of this.faqCategoriesAndQuestionsMapping) {
			questions.forEach((childPanel: IFaqQuestion) => {
				childPanel.active = false;
			});
		}
		this.categories.forEach((parentPanel: IFaqCategory) => {
			parentPanel.active = false;
		});
		question.active = true;
		category.active = true;

		const questionElement = document.getElementById(question.id.toString());

		setTimeout(() => {
			questionElement.scrollIntoView({
				behavior: 'smooth',
				block: 'center'
			});
		}, 300);*/
    question.active = true;
    category.active = true;
  }

  public onEmptyQuery(): void {
    this._loadFaq();
  }

  public onSearchResultClick(result: SearchResult): void {
    const { id, category__id } = result as Faq;
    this._loadFaq(+category__id, +id);
  }

  public showContactUsForm(): void {
    this._modalService.create({
      nzContent: ContactUsModalComponent,
      nzCentered: true,
      nzClosable: false,
      nzWrapClassName: 'modal-wrap',
      nzWidth: 554
    });
  }

  public onEventsListPageIndexChange(page: number): void {
    this.pagination.page = page;
    this._loadFaq();
  }

  public getQuestionsByCategoryId(categoryId: number): IFaqQuestion[] {
    return this.faqCategoriesAndQuestionsMapping.get(categoryId);
  }

  private _openFaqPanel(categoryId: number, questionId: number): void {
    const category = this.categories?.find((category: IFaqCategory) => category?.id === categoryId);
    if (category) {
      category.active = true;
    }
    const question = this.faqCategoriesAndQuestionsMapping
      .get(categoryId)
      ?.find((question: IFaqQuestion) => question?.id === questionId);
    if (question) {
      question.active = true;
    }
  }

  private _loadFaq(categoryId?: number, questionId?: number): void {
    this.isLoading = true;
    const filters: IFilter[] = [];
    if (!!categoryId) {
      filters.push({ name: 'category_id', value: categoryId.toString() });
    }

    this._faqService
      .getFaqCategories(this.pagination, filters)
      .pipe(
        tap((response: IGetPaginatedResponse<IFaqCategory[]>) => {
          this.total = response.total_pages * response.per_page;
          this.categories = response.results.filter((faq: IFaqCategory) => faq.language === this.selectedLanguageId);
          this.categories.forEach((category: IFaqCategory) => this._loadQuestionsByCategoryId(category.id, questionId));
        }),
        finalize(() => {
          this.isLoading = false;
        }),
        catchError(() => of(null)),
        takeUntil(this._onDestroy$)
      )
      .subscribe();
  }

  private _loadQuestionsByCategoryId(categoryId: number, questionId: number): void {
    this.isLoading = true;

    const filters: IFilter[] = [];
    if (!!questionId) {
      filters.push({ name: 'question_id', value: questionId.toString() });
    }

    this._faqService
      .getQuestionsByCategoryId(categoryId, filters)
      .pipe(
        tap((response: IGetPaginatedResponse<IFaqQuestion[]>) => {
          this.faqCategoriesAndQuestionsMapping.set(categoryId, response.results);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
        catchError(() => of(null)),
        takeUntil(this._onDestroy$)
      )
      .subscribe(() => {
        // If we have both a category and a question - we need to open this panel
        if (!!categoryId && !!questionId) {
          this._openFaqPanel(categoryId, questionId);
        }
      });
  }

  private _setLangListener(): void {
    this._translateService.onLangChange.pipe(takeUntil(this._onDestroy$)).subscribe(() => {
      this._loadFaq();
    });
  }
}
